// FaceBiometric.jsx

import React, { useState } from 'react';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useFaceBio } from './useFaceBio';

import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Paper,
  Grid,
  Avatar,
  Fade,
  CircularProgress,
  Backdrop
} from '@material-ui/core';

import CenterFocusStrongIcon from '@material-ui/icons/CenterFocusStrong';
import FaceIcon from '@material-ui/icons/Face';
import AccessTimeIcon from '@material-ui/icons/AccessTime';

import FaceScanner from './components/FaceScanner';
import Usertable from './components/Usertable';
import DebounceTextField from 'src/components/DebounceTextField';

import { motion } from 'framer-motion';

const FaceBiometric = () => {
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {
    openBioSelfie,
    handleOpen,
    handleClose,
    webcamRef,
    canvasRef,
    capture,
    bioID,
    handleSetBioID,
    allowCapture,
    initFaceMesh,
    selected,
    setSelected,
    handleSelectAll,
    handleSelect,
    tableData,
    setTableData,
    resEmpData,
    search,
    setSearch,
    loadingTable,
    setLoadingTable
  } = useFaceBio();

  const handleSearch = query => {
    setSearch(query);

    if (query) {
      const filtered = resEmpData.filter(item =>
        item.sSub?.toLowerCase().includes(query.toLowerCase())
      );

      setTableData(filtered);
    } else {
      setTableData(resEmpData);
    }
  };

  return (
    <>
      <motion.div
        initial={{
          opacity: 0,
          y: 40
        }}
        animate={{
          opacity: 1,
          y: 0
        }}
        whileHover={{
          y: -8,
          scale: 1.02
        }}
        transition={{
          type: 'spring',
          stiffness: 220,
          damping: 18
        }}
      >
        <Paper
          elevation={4}
          onClick={handleOpen}
          style={{
            cursor: 'pointer',
            borderRadius: 20,
            overflow: 'hidden',
            background: `linear-gradient(
              135deg,
              ${theme.palette.primary.dark} 0%,
              ${theme.palette.primary.main} 50%,
              ${theme.palette.primary.light} 100%
            )`,
            color: theme.palette.primary.contrastText,
            padding: 24,
            position: 'relative',
            transition: '0.3s'
          }}
        >
          <motion.div
            animate={{
              scale: [1, 1.15, 1],
              opacity: [0.15, 0.3, 0.15]
            }}
            transition={{
              duration: 5,
              repeat: Infinity
            }}
            style={{
              position: 'absolute',
              top: -120,
              right: -120,
              width: 400,
              height: 400,
              borderRadius: '50%',
              background:
                'radial-gradient(circle, rgba(255,255,255,.35), transparent)',
              filter: 'blur(20px)',
              pointerEvents: 'none'
            }}
          />

          <motion.div
            animate={{
              y: [0, -25, 0],
              x: [0, -15, 0]
            }}
            transition={{
              duration: 6,
              repeat: Infinity
            }}
            style={{
              position: 'absolute',
              top: 40,
              right: 100,
              width: 120,
              height: 120,
              borderRadius: '50%',
              background: 'rgba(255,255,255,.12)',
              filter: 'blur(2px)',
              pointerEvents: 'none'
            }}
          />

          <motion.div
            animate={{
              y: [0, 20, 0],
              x: [0, 10, 0]
            }}
            transition={{
              duration: 4,
              repeat: Infinity
            }}
            style={{
              position: 'absolute',
              bottom: 30,
              right: 50,
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: 'rgba(255,255,255,.15)',
              filter: 'blur(4px)',
              pointerEvents: 'none'
            }}
          />

          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <Avatar
                style={{
                  width: 80,
                  height: 80,
                  background: `linear-gradient(
                    135deg,
                    ${theme.palette.secondary.main} 0%,
                    ${theme.palette.primary.light} 100%
                  )`
                }}
              >
                <CenterFocusStrongIcon style={{ fontSize: 65 }} />
              </Avatar>
            </Grid>

            <Grid item xs>
              <Typography
                variant="h4"
                style={{
                  fontWeight: 700,
                  letterSpacing: 1
                }}
              >
                AI SELFIE TIME IN/OUT
              </Typography>

              <Typography
                variant="body1"
                style={{
                  opacity: 0.9,
                  marginTop: 8
                }}
              >
                Face Recognition Attendance System
              </Typography>

              <Box display="flex" alignItems="center" mt={2} gridGap={16}>
                <Box display="flex" alignItems="center" gridGap={6}>
                  <FaceIcon style={{ fontSize: 18 }} />

                  <Typography variant="body2">Face Verification</Typography>
                </Box>

                <Box display="flex" alignItems="center" gridGap={6}>
                  <AccessTimeIcon style={{ fontSize: 18 }} />

                  <Typography variant="body2">Real-Time IN / OUT</Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item>
              {/* <motion.div
                animate={{
                  scale: [1, 1.05, 1]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity
                }}
              > */}
              <Box
                px={3}
                py={1}
                borderRadius={30}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Typography
                  variant="button"
                  style={{
                    color: theme.palette.primary.contrastText,
                    fontWeight: 700,
                    letterSpacing: 1
                  }}
                >
                  OPEN SYSTEM
                </Typography>
              </Box>
              {/* </motion.div> */}
            </Grid>
          </Grid>
        </Paper>
      </motion.div>

      {loadingTable ? (
        <Backdrop
          open={loadingTable}
          style={{
            zIndex: 9999
          }}
        >
          <CircularProgress color="primary" />
        </Backdrop>
      ) : (
        <Dialog
          open={openBioSelfie}
          onClose={handleClose}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 16
              }}
            >
              <div>
                <Typography variant="h4">AI Selfie Time IN/OUT</Typography>

                <Typography variant="body2" color="textSecondary">
                  AI-powered biometric verification and attendance tracking
                </Typography>
              </div>

              <div style={{ width: 400 }}>
                <DebounceTextField
                  variant="outlined"
                  size="medium"
                  fullWidth
                  label="Search"
                  onChange={e => handleSearch(e.target.value)}
                  value={search}
                />
              </div>
            </div>
          </DialogTitle>

          <DialogContent>
            <Box display="flex" gridGap={16} width="100%">
              <Box
                flex={1}
                minHeight={400}
                border={`1px solid ${theme.palette.divider}`}
                borderRadius={12}
                p={2}
                display="flex"
                alignItems="center"
                justifyContent="center"
                bgcolor={theme.palette.background.paper}
              >
                <FaceScanner
                  webcamRef={webcamRef}
                  canvasRef={canvasRef}
                  capture={capture}
                  bioID={bioID}
                  handleSetBioID={handleSetBioID}
                  allowCapture={allowCapture}
                  initFaceMesh={initFaceMesh}
                />
              </Box>

              <Box
                flex={1}
                minHeight={400}
                border={`1px solid ${theme.palette.divider}`}
                borderRadius={12}
                p={2}
                display="flex"
                flexDirection="column"
                bgcolor={theme.palette.background.paper}
              >
                <Usertable
                  tableData={tableData}
                  selected={selected}
                  setSelected={setSelected}
                  handleSelectAll={handleSelectAll}
                  handleSelect={handleSelect}
                />
              </Box>
            </Box>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default FaceBiometric;
